import React, { useMemo, useState } from "react";
import {
  IEEG_LOCALIZE_ALGORITHM_ID,
  IEEG_MODEL_CARD_URI,
  generateIeegNeighborMontage,
  groupIeegContactsForTrends,
  parseIeegContacts,
  serializeIeegContactsCsv,
  type IeegMontageMode,
} from "../utils/ieegLocalizeHelpers";
import {
  FROZEN_IEEG_EXCLUDED_USE,
  FROZEN_IEEG_INTENDED_USE,
  IEEG_IFU_UI_BADGE,
  IEEG_IFU_UI_SUMMARY,
} from "../utils/assistiveIfuFreeze";
import { EegDockPanelLabeling } from "./EegDockPanelLabeling";

export type IeegLocalizeDockPanelProps = {
  channelNames?: string[];
  /** Optional preloaded XYZ rows (digitized import). */
  contacts?: Array<{ name: string; x: number; y: number; z: number }>;
  onApplyMontagePairs?: (
    pairs: Array<{ label: string; active: string; reference: string }>,
  ) => void;
};

function demoContactsFromNames(names: string[]) {
  return names.slice(0, 24).map((name, index) => ({
    name,
    x: Math.cos(index / 3) * 40,
    y: Math.sin(index / 3) * 40,
    z: -20 - (index % 8) * 5,
  }));
}

export default function IeegLocalizeDockPanel({
  channelNames = [],
  contacts: contactsProp,
  onApplyMontagePairs,
}: IeegLocalizeDockPanelProps) {
  const [mode, setMode] = useState<IeegMontageMode>("longitudinal");
  const [jsonDraft, setJsonDraft] = useState("");

  const contacts = useMemo(() => {
    if (contactsProp && contactsProp.length > 0) {
      return parseIeegContacts(contactsProp);
    }
    if (jsonDraft.trim()) {
      try {
        const parsed = JSON.parse(jsonDraft) as Array<{
          name: string;
          x: number;
          y: number;
          z: number;
        }>;
        return parseIeegContacts(parsed);
      } catch {
        return [];
      }
    }
    const seed =
      channelNames.length > 0
        ? channelNames
        : ["A1", "A2", "A3", "A4", "B1", "B2", "B3", "B4"];
    return parseIeegContacts(demoContactsFromNames(seed));
  }, [channelNames, contactsProp, jsonDraft]);

  const pairs = useMemo(
    () => generateIeegNeighborMontage(contacts, mode),
    [contacts, mode],
  );
  const trendGroups = useMemo(
    () => groupIeegContactsForTrends(contacts),
    [contacts],
  );

  return (
    <div className="eeg-dock-panel" data-testid="ieeg-localize-dock">
      <EegDockPanelLabeling
        data-testid="ieeg-localize-ifu"
        badge={IEEG_IFU_UI_BADGE}
        summary={IEEG_IFU_UI_SUMMARY}
        intendedUse={FROZEN_IEEG_INTENDED_USE}
        excludedUse={FROZEN_IEEG_EXCLUDED_USE}
        extraDetails={`Algorithm ${IEEG_LOCALIZE_ALGORITHM_ID} · Model card ${IEEG_MODEL_CARD_URI}`}
      />

      <div className="eeg-dock-panel-chrome__toolbar" style={{ gap: 8 }}>
        <label>
          Montage mode{" "}
          <select
            data-testid="ieeg-montage-mode"
            value={mode}
            onChange={(e) => setMode(e.target.value as IeegMontageMode)}
          >
            <option value="longitudinal">Longitudinal neighbor</option>
            <option value="neighbor">Neighbor + skip-one</option>
            <option value="grid_row">Grid row</option>
          </select>
        </label>
        <button
          type="button"
          data-testid="ieeg-apply-montage"
          onClick={() => onApplyMontagePairs?.(pairs)}
        >
          Apply iEEG montage ({pairs.length})
        </button>
        <a
          data-testid="ieeg-export-contacts"
          href={`data:text/csv;charset=utf-8,${encodeURIComponent(
            serializeIeegContactsCsv(contacts),
          )}`}
          download="ieeg-contacts.csv"
        >
          Export contacts CSV
        </a>
      </div>

      <details open>
        <summary>
          Contacts ({contacts.length}) · trend groups ({trendGroups.length})
        </summary>
        <ul data-testid="ieeg-contact-list">
          {contacts.slice(0, 40).map((c) => (
            <li key={c.name}>
              {c.name} · {c.kind} · shaft {c.shaft ?? "—"} · (
              {c.x.toFixed(1)}, {c.y.toFixed(1)}, {c.z.toFixed(1)})
            </li>
          ))}
        </ul>
      </details>

      <details>
        <summary>Paste digitized JSON (name,x,y,z)</summary>
        <textarea
          data-testid="ieeg-contacts-json"
          rows={4}
          value={jsonDraft}
          onChange={(e) => setJsonDraft(e.target.value)}
          placeholder='[{"name":"A1","x":1,"y":2,"z":-30}]'
          style={{ width: "100%" }}
        />
      </details>

      <details>
        <summary>Generated bipolar pairs ({pairs.length})</summary>
        <ul data-testid="ieeg-montage-pairs">
          {pairs.slice(0, 60).map((p) => (
            <li key={p.label}>{p.label}</li>
          ))}
        </ul>
      </details>

      <details>
        <summary>iEEG trend groups</summary>
        <ul data-testid="ieeg-trend-groups">
          {trendGroups.map((g) => (
            <li key={g.groupId}>
              {g.label}: {g.contacts.join(", ")}
            </li>
          ))}
        </ul>
      </details>
    </div>
  );
}
